import { useCallback, useEffect, useState } from "react";
type _CSSProperties = import("react").CSSProperties;

// ############################ RANDOM RANGE ############################
/** Create some random number between the specified ranges
 * @param _min Range's start (inclusive) `default: 0`
 * @param _max Range's end (inclusive) `default: 10`
 * @param _decimal Whether to output a `int` number or a `float` number `default: false`
 * @returns An `int` random
 */
export function randomRange(_min?: number, _max?: number, _decimal?: boolean) {
  _max = _max ?? 10;
  _min = _min ?? 0;

  var _diff = _max - _min;

  if (!_decimal) return Math.round(_min + _diff * Math.random());
  return _min + _diff * Math.random();
}

// ############################ BOUNDARIES ############################

/** Returns a valid number between some specific limits (inclusive) without letting it to overflow
 * @param _value Value to check
 * @param _min Inferior limit, pass `undefined` for limitless
 * @param _max Superior limit, pass `undefined` for limitless
 * @param _wrap Jump the value to the other limit in case of overflow `default: false`
 * @returns A `number` between those limits
 */
export function boundaries(
  _value: number,
  _min?: number,
  _max?: number,
  _wrap?: boolean
) {
  _min = _min ?? _value;
  _max = _max ?? _value;

  if (!_wrap) {
    if (_value < _min) return _min;
    if (_value > _max) return _max;
    return _value;
  }

  if (_value < _min) return _max;
  if (_value > _max) return _min;
  return _value;
}

// ############################ APPROACH ############################
/** Approach the `_start` param to the `_end` param without overflowing it
 * @param _start Start value
 * @param _end End value
 * @param _amount Step to take `default: 1`
 * @returns The new value `_start` one `_amount` step closer to `_end`
 */
export function approach(_start: number, _end: number, _amount?: number) {
  _amount = _amount ?? 1;

  if (_start < _end) return Math.min(_start + _amount, _end);
  else return Math.max(_start - _amount, _end);
}

// ############################ PARSE CSS ############################
/** Converts an `CSSProperties` object into `string` with correct CSS' sintax,
 * this can be inserted direclty to the `css` function from `styled-components`
 * @param JS `CSSProperties` object to convert into `string`
 * @returns parsed `string` with CSS' sintax
 */
export const parseCSS = (JS?: _CSSProperties) => {
  if (JS === undefined) return "";
  var cssString = "";

  for (const objectKey in JS) {
    cssString +=
      objectKey.replace(/([A-Z])/g, (g) => `-${g[0]?.toLowerCase()}`) +
      ": " +
      JS[objectKey] +
      ";\n";
  }

  return cssString;
};

// ############################ SEARCH ALL REGEXP ############################
/** Provides a `RegExp` to seach all coincidences of a specified `string`.
 * To replace every entry use `$1$2$3X` where `X` represents the replacement
 * @param input The `string` to search for within the `RegExp`
 * @returns The `RegExp` ready to use
 */
export function searchAll(input: string): RegExp {
  if (input === "") input = " ";

  // Escape any REGEXP special character for integrity
  input = input.replace(CONSTANTS._REGEXPS._escapeRegExp, "\\$&");

  // Create the REGEXP
  // (?:([^\\])(?:X))|(?:([^\\]\\\\)(?:X))|(?:^X)|(?:^(\\\\)(?:X))
  return new RegExp(
    "(?:([^\\\\])(?:" +
      input +
      "))|(?:([^\\\\]\\\\\\\\)(?:" +
      input +
      "))|(?:^" +
      input +
      ")|(?:^(\\\\\\\\)(?:" +
      input +
      "))",
    "g"
  );
}

// ############################ ESCAPE CHARACTERS REGEXP ############################
/** Replaces every escaped character from a given `string`; this is, converts a `\*` into `*`
 * @param input The `string` to manipulate
 * @returns A `string` sucessfully processed
 */
export function escapeChars(input: string): string {
  return input.replace(CONSTANTS._REGEXPS._escapedCharacters, "$1");
}

// ############################ FORMAT TEXT ############################
/** Applies a format style to the `string` passed, removing every unnecesary (duplicated) whitespace chars.
 * It also allows to add *manual linebreaks' for better control of text.
 * @param _text Text `string` to work with and apply formatting
 * @param _newLine `string` representing a manual breakline. If an empty string is given `""`, *it will add nothing*. `default: ";;"`
 * @param _translate Translate text with `i18n` **(DEACTIVATED)** `default: true`
 * @returns A `string` with one space character between words, every linebreak (`\n`) removed and any manual one replaced.
 * @version 2.0
 */
export function formatText(
  _text: string,
  _newLine?: string,
  _translate?: boolean
): string {
  _newLine = _newLine ?? ";;";
  _translate = _translate ?? true;

  // Replace EVERY WHITESPACE CHARACTER with a single space " ".
  _text = _text.replace(CONSTANTS._REGEXPS._formatText, " ");

  if (_newLine === "") return _text;

  // (?: )*;;(?!\\)(?: )*
  const temp = new RegExp("(?: )*" + _newLine + "(?!\\\\)(?: )*", "g");

  // Replace EVERY NEWLINE STRING with a LINE BREAK character '\n'
  _text = _text.replace(temp, "\n");
  return _text;
}

// ############################ STYLE TEXT ############################
/**
 * Proporciona los `props` para la función `styleText`
 */
export type styleTextOptions = {
  /**Texto con el cual trabajar*/
  _text: string;
  /**Todo el texto que esté envuelto en este `string` tendrá aspecto **BOLD** `default: "*"`*/
  _boldWrap?: string;
  /**Todo el texto que esté envuelto en este `string` tendrá aspecto *ITALICS* `default: "_"`*/
  _italicsWrap?: string;
  /**Todo el texto que esté envuelto en este `string` tendrá aspecto ~~CROSS~~ `default: "~"`*/
  _crossWrap?: string;
  /**Todo el texto que esté envuelto en este `string` será transparente al 50% `default: "#"`*/
  _muteWrap?: string;
  /**Todo el texto que esté envuelto en este `string` estará subrayado `default: "&"`*/
  _underlineWrap?: string;
  /**Todo el texto que esté envuelto en este `string` será de color `emphasis` `default: "$"`*/
  _emphasisWrap?: string;
  /**Lista que contiene los colores con los cuales colorear el texto de `emphasis`, si hay más selectores en el texto que colores en esta lista, los colores comenzarán a ciclarse `default: ["#1757cd"]`*/
  _emphasisColors?: Array<string>;
  /**Todo el texto que esté envuelto en este `string` se convertirá en LINK (< a/>) `default: "="`*/
  _linkWrap?: string;
  /**Lista que contiene las URLs para los links insertados mediante `linkWrap`, si hay más links en el texto que URLs en esta lista, las URLs comenzarán a ciclarse `default: ["/"]`*/
  _linkList?: Array<string>;
  /**Lista que contiene los estilos para los links insertados mediante `linkWrap`, puede ser texto para un `className` o directamente `CSSProperties`, si hay más links en el texto que estilos en esta lista, los estilos comenzarán a ciclarse `default: "[{}]"`*/
  _linkStyles?: Array<string | _CSSProperties>;
  /**Todo el texto que esté envuelto en este `string` tendrá tamaño de letra personalizado `default: "^"`*/
  _fontSizeWrap?: string;
  /**Lista que contiene los tamaños de letra para el texto seleccionado `fontSizeWrap`, si hay más selectores en el texto que tamaños de letra en esta lista, los tamaños comenzarán a ciclarse `default: ["16px"]`*/
  _fontSizeList?: Array<string | number>;
  /**Todo el texto que esté envuelto en este `string` tendrá estilo personalizado `default: "@"`*/
  _customWrap?: string;
  /**Lista que contiene los estilos personalizados para `customWrap`, puede ser texto para un `className` o directamente `CSSProperties`, si hay más selectores en el texto que estilos en esta lista, los estilos comenzarán a ciclarse `default: [{}]`*/
  _customStyles?: Array<string | _CSSProperties>;
  /**Integración con `formatText` para aplicar formato automáticamente `default: true`*/
  _formatText?: boolean;
  /**Integración con `formatText` para ingresar saltos de línea manuales `default: ";;"`*/
  _formatText_newLine?: string;
  /**Integración con `formatText` para traducir el texto con i18n **(DESACTIVADO)** `default: true`*/
  _formatText_translate?: boolean;
};

/**
 * Estiliza una cadena de texto convirtiéndola en `< span/>` con formato híbrido personalizado similar a `markdown` pero con más aditamientos
 *
 * Permite agregar colores, links (`< a/>`), opacidad y hasta seleccionar fragmentos de texto con un estilo desde cero
 *  @returns Un elemento `JSX` con el texto estilizado mediante `CSS`
 */
export function styleText(props: styleTextOptions) {
  const _formatText = props._formatText ?? true,
    _formatText_newLine = props._formatText_newLine ?? ";;",
    _formatText_translate = props._formatText_translate ?? true;

  if (_formatText) {
    props._text = formatText(
      props._text,
      _formatText_newLine,
      _formatText_translate
    );
  }

  const options: {
    [key: string]: string | (string | _CSSProperties)[] | (string | number)[];
  } = {
    boldWrap: props._boldWrap ?? "*",
    italicsWrap: props._italicsWrap ?? "_",
    crossWrap: props._crossWrap ?? "~",
    muteWrap: props._muteWrap ?? "#",
    underlineWrap: props._underlineWrap ?? "&",
    emphasisWrap: props._emphasisWrap ?? "$",
    emphasisColors: props._emphasisColors ?? ["#1757cd"],
    linkWrap: props._linkWrap ?? "=",
    linkList: props._linkList ?? ["/"],
    linkStyles: props._linkStyles ?? [{}],
    fontSizeWrap: props._fontSizeWrap ?? "^",
    fontSizeList: props._fontSizeList ?? ["16px"],
    customWrap: props._customWrap ?? "@",
    customStyles: props._customStyles ?? [{}],
  };

  const fields = [
    "bold",
    "italics",
    "cross",
    "mute",
    "underline",
    "emphasis",
    "link",
    "fontSize",
    "custom",
  ];

  const refill: {
    [key: string | number]: string[];
  } = {
    bold: ["<span style='font-weight: 700;'>", "</span>"],
    italics: ["<span style='font-style: italic;'>", "</span>"],
    cross: ["<span style='text-decoration: line-through;'>", "</span>"],
    mute: ["<span style='opacity: 40%;'>", "</span>"],
    underline: ["<span style='text-decoration: underline'>", "</span>"],

    emphasis: [`<span style='color: ${options.emphasisColors[0]}'>`, "</span>"],
    link: [`<a class='' href=''> `, "</a>"],
    fontSize: [`<span style=''>`, "</span>"],
    custom: [`<span style=''>`, `</span>`],
  };

  const regEx: {
    [key: string]: RegExp;
  } = {};

  for (let i = 0; i < fields.length; i++) {
    var temp = options[`${fields[i]}Wrap`]!;
    if (typeof temp !== "string") temp = "";
    temp = temp.replace(/./gim, "$&");

    try {
      regEx[fields[i]!] = new RegExp("(?<!\\\\)\\" + temp, "gim");
    } catch (e) {
      regEx[fields[i]!] = new RegExp("\\" + temp, "gim");
    }
  }

  const sections: {
    [key: string]: any;
  } = {};

  for (let i = 0; i < fields.length; i++) {
    sections[fields[i]!] = new Array<number>();
  }

  for (let i = 0; i < fields.length; i++) {
    while (true) {
      var match = regEx[fields[i]!]!.exec(props._text);
      if (match === null) break;

      sections[fields[i]!].push(match.index);
    }
  }

  const process = new Array<Array<string | number>>();

  for (let i = 0; i < fields.length; i++) {
    for (let j = 0; j < sections[fields[i]!].length; j += 2) {
      const pos1 = sections[fields[i]!][j];
      const pos2 = sections[fields[i]!][j + 1];

      if (pos2 !== undefined) {
        process[pos1] = [`${fields[i]}`, 0];
        process[pos2] = [`${fields[i]}`, 1];
      }
    }
  }

  var newText = "";
  var prevCut = 0;

  var lastColor = 0;

  var lastLink = 0;
  var lastLinkStyle = 0;

  var lastFontSize = 0;

  var lastCustomStyle = 0;

  for (let i = 0; i < process.length; i++) {
    if (process[i] !== undefined) {
      if (process[i][0] === "emphasis" && process[i][1] === 0) {
        newText +=
          props._text.slice(prevCut, i) +
          `<span style='color: ${options.emphasisColors[lastColor]}'>`;

        if (options.emphasisColors![lastColor + 1] !== undefined) lastColor++;
      } else if (process[i][0] === "link" && process[i][1] === 0) {
        var newLinkClass = "";
        var newLinkStyle = "";
        var nl = options.linkStyles![lastLinkStyle];

        if (typeof nl === "string") {
          newLinkClass = nl;
        } else if (typeof nl !== "number") {
          newLinkStyle = parseCSS(nl);
        }

        newText +=
          props._text.slice(prevCut, i) +
          `<a class='${newLinkClass}' href=${
            options.linkList![lastLink]
          } style='${newLinkStyle}'>`;

        if (options.linkList![lastLink + 1] !== undefined) lastLink++;
        if (options.linkStyles![lastLinkStyle + 1] !== undefined)
          lastLinkStyle++;
      } else if (process[i][0] === "fontSize" && process[i][1] === 0) {
        var newFontSize = "";
        var fs = options.fontSizeList![lastFontSize];

        if (typeof fs === "string") {
          newFontSize = fs;
        } else if (typeof fs === "number") {
          newFontSize = `${fs}px`;
        }

        newText +=
          props._text.slice(prevCut, i) +
          `<span style='font-size: ${newFontSize}'>`;

        if (options.fontSizeList![lastFontSize + 1] !== undefined)
          lastFontSize++;
      } else if (process[i][0] === "custom" && process[i][1] === 0) {
        var customClass = "";
        var customStyle = "";

        var cc = options.customStyles![lastCustomStyle];
        if (typeof cc === "string") {
          customClass = cc;
        } else if (typeof cc !== "number") {
          customStyle = parseCSS(cc);
        }

        newText +=
          props._text.slice(prevCut, i) +
          `<span className='${customClass}' style='${customStyle}'>`;

        if (options.customStyles![lastCustomStyle + 1] !== undefined)
          lastCustomStyle++;
      } else {
        var temp2 = process[i]![1]!;
        if (typeof temp2 !== "number") temp2 = 0;

        newText +=
          props._text.slice(prevCut, i) +
          refill[process[i]![0]!.toString()]![temp2];
      }
      prevCut = i + 1;
    }
  }
  newText += props._text.slice(prevCut);

  return <span dangerouslySetInnerHTML={{ __html: newText }} />;
}

// ############################ CREATE TUPLE ############################
/**
 * Creates a tuple from the given `Array`
 * @param args Array from which create the tuple
 * @returns A `Tuple` type with the contents from `args`
 */
export const createTuple = <T extends unknown[]>(args: [...T]): T => args;

// ############################ MERGE OBJECT TYPES ############################
type noOptional<T extends object> = { [P in keyof T]-?: T[P] };

type test = { a: number; b?: string; c?: number | string };
type test2 = { a?: string; b: string; d: string[] };

// type combine<T1 extends object, T2 extends object> = {   };

// type comb< > = A;

// type aa = comb<test>;

type OptionalPropertyNames<T> = {
  [K in keyof T]-?: {} extends { [P in K]: T[K] } ? K : never;
}[keyof T];

type SpreadProperties<L, R, K extends keyof L & keyof R> = {
  [P in K]: L[P] | Exclude<R[P], undefined>;
};

type Id<T> = T extends infer U ? { [K in keyof U]: U[K] } : never;

type SpreadTwo<L, R> = Id<
  Pick<L, Exclude<keyof L, keyof R>> &
    Pick<R, Exclude<keyof R, OptionalPropertyNames<R>>> &
    Pick<R, Exclude<OptionalPropertyNames<R>, keyof L>> &
    SpreadProperties<L, R, OptionalPropertyNames<R> & keyof L>
>;

type Spread<A extends readonly [...any]> = A extends [infer L, ...infer R]
  ? SpreadTwo<L, Spread<R>>
  : unknown;

type Foo = Spread<[{ a: string }, { a?: number }]>;

export function merge<A extends object[]>(...a: [...A]) {
  return Object.assign({}, ...a) as Spread<A>;
}

// ############################ PARSE ID ############################
/**
 * Parses any given `string` to match an alphanumeric type ID, replacing any other char
 * with a hyphen `-` and removes punctuation marks from `Á É Í Ó Ú á é í ó ú Ä Ë Ï Ö Ü ä ë ï ö ü Ý ý ÿ` etc.
 * @param val `string` to transform
 * @returns Transformed `string`
 */
export function parseID(val: string): string {
  return val
    .trim()
    .toLowerCase()
    .replace(/[\u00C0-\u00C6]/gim, "a")
    .replace(/[\u00C7]/gim, "c")
    .replace(/[\u00C8-\u00CB]/gim, "e")
    .replace(/[\u00CC-\u00CF]/gim, "i")
    .replace(/[\u00D0]/gim, "d")
    .replace(/[\u00D1]/gim, "n")
    .replace(/[\u00D2-\u00D6\u00D8]/gim, "o")
    .replace(/[\u00D9-\u00DC]/gim, "u")
    .replace(/[\u00DD-\u00DF\u00FF]/gim, "y")
    .replace(/[\s\W_]+/gim, "-");
}

// ############################ PARSE PRICE ############################
/**
 * Parses any given `number` to match an price-based formatting, including `$` at start
 * and a `,` for separation every thousand
 * @param number `number` to format, it can be an `string` to
 * @param showDollar Whether or not show a `$` at the start of the number
 * @param decimalMax Specify the maximum number of decimals shown, limiting the length
 * @param decimalMin Specify the minimum number of decimals shown, autofilled with `0`
 * @returns Transformed `string` with formatting, or the same `string` if it does not holds a number
 */
export function parsePrice(
  number: string | number,
  showDollar: boolean = true,
  decimalMax: number = 0,
  decimalMin: number = 0
): string {
  var temp: string | number = parseNumber(number.toString(), true);
  var decimal = temp.toString().replace(/([0-9]*\.*)^([0-9])+/gim, "");
  var dummy = "";
  while (dummy.length < decimalMin) dummy += "0";
  decimal = decimal.slice(1, decimalMax + 1);
  decimal += dummy.slice(decimal.length);

  temp = Number.parseInt(temp.toString()).toString();
  var aux = temp;
  for (let i = temp.length - 4; i >= 0; i -= 3) {
    aux = aux.slice(0, i + 1) + "," + aux.slice(i + 1);
  }

  temp =
    (showDollar ? "$" : "") + aux + (decimal.length === 0 ? "" : "." + decimal);

  return temp;
}

// ############################ PARSE NUMBER ############################
/**
 * Extended version of `Number.parseInt` & `Number.parseFloat`, it accepts any kind of `string` and does
 * not returns `NaN`, in that case returns `0`.
 * @param str String to parse.
 * @param decimal Whether to use `parseFloat` or `parseInt`, defaults to `false`.
 * @returns A number correctly parsed, or a default `0` if it catches a `NaN`.
 */
export function parseNumber(str: string, decimal: boolean = false): number {
  str = str.toString().replace(/[^0-9.]+/gim, "");
  if (decimal) return Number.parseFloat(str || "0");
  else return Number.parseInt(str || "0");
}

// ############################ CAPITALIZE ############################
/**
 * Converts the first character of a given string into UPPER case, or the first letter
 * for every word in it (if specified).
 * @param str `string` to work with.
 * @param all Whether or not capitalize every word in `string`
 */
export function capitalize(str: string, all: boolean = false): string {
  if (!all) return str[0].toUpperCase() + str.slice(1);
  else return "WIP";
}

// ############################ MAP OBJECT ############################
/**
 * Calls the defined `callback` with each property from the given `object` and returns
 * another object with identical properties but holding the return value from the `callback`.
 * Similar to `Array.map` but with an object.
 * @param object Object to operate with.
 * @param callback Callback to apply to each property value from the given object.
 * @returns An `object` with the same properties as the given object but containing
 * the return value of the callback applied with it
 */
export function mapObject<T, K>(
  object: T,
  callback: (property: string, index: number, obj: typeof object) => K
) {
  // @ts-ignore
  var temp: {
    [Property in keyof T]: K;
  } = {};

  var i: number = 0;
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      temp[key] = callback(key, i, object);
    }
    ++i;
  }

  return temp;
}

// ############################ AS TYPE ############################
/**
 * Trick `typescript` into *'misinterpreting'* a variable's type as another type passed.
 * ***WARNING:*** as stated, this could lead to *accidental-intentional* errors in runtime, but it also
 * serves like a *'typescript disable'* in certain specific cases.
 * @param val Any variable to misinterpretate, the value will stay the same
 * @param T `Type` parameter to which trick typescript into
 * @returns The same value passed in `val` but interpreted like the new `Type`
 */
export function asType<T extends any>(val: any): T {
  var temp: T = val;
  return temp;
}

// ############################ HANDLE ERROR ############################
/**
 * Auto-error handler, prints to the console any error passed. Also shows an `window.alert`
 * for user interpretation.
 * @param error Any object containing error data.
 * @param params Which params interpretate in console.
 * @param loc `string` telling at which point the error ocurred (for user interpretation).
 * @param alert Whether or not to show the `window.alert`
 */
export function handleError(
  error: any,
  params: string | Array<string> = "",
  loc: string = "",
  alert: boolean = true
) {
  if (alert) window.alert("Something went wrong... " + loc);
  if (params !== "") {
    if (typeof params === "string") {
      console.log(error?.[params]);
    } else {
      params.forEach((key) => console.log(key, error?.[params[key]]));
    }
  } else console.log(error);
}

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
(""); // ############################ HOOKS ############################

// ############################ WINDOW SCROLL ############################
/**
 * Hook que reacciona al scroll vertical de la pantalla, de forma similar a como lo hace el componente `Header`
 * @param threshold Marca el límite para el scroll vertical `default: 100`
 * @returns `true` cuando se hace scroll vartical pasado el punto de `threshold`, `false` cuando no
 */
export const useWindowScroll = (threshold: number = 100) => {
  const [scrolled, setScrolled] = useState(false);

  const changeScroll = useCallback(() => {
    if (window.scrollY > threshold) setScrolled(true);
    else setScrolled(false);
  }, [threshold]);

  useEffect(() => {
    document.addEventListener("scroll", changeScroll);
  }, [changeScroll]);

  return scrolled;
};

// ############################ ON SCREEN ############################
/**
 * Hook para conocer si un elemento está a la vista (en pantalla)
 * @param ref referencia hacia el elemento al cual observar
 * @returns `true` si el elemento se encuentra dentro de la pantalla, `false` si no lo está
 */
export function useOnScreen(ref: any) {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) =>
      setIntersecting(entry.isIntersecting)
    );
    observer.observe(ref.current);
    // Remove the observer as soon as the component is unmounted
    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return isIntersecting;
}

// ############################ SCROLL TO TOP ############################
/** Scrolls the window all the way to the top when rendering */
export function ScrollToTopOnMount() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return null;
}

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
(""); // ############################ CONSTANTS ############################
export const CONSTANTS = {
  _COLORS: {
    _blue: "#0d6efd",
    _indigo: "#35008b", // Visited
    _purple: "#6f42c1",
    _pink: "#d63384",
    _red: "#dc3545", // Active - Danger - Selected Links
    _orange: "#fd7e14", // Hover
    _yellow: "#ffc107", // Warning
    _green: "#198754", // Success
    _teal: "#20c997",
    _cyan: "#0dcaf0", // Emphasis - Info - Links
    _black: "#000000", // Black - Bold Text
    _dark: "#212529", // Dark - Alternative Text
    _white: "#f8f9fa",
    _lightgray: "#ced4da",
    _gray: "#6c757d", // Secondary
    _darkgray: "#343a40",
    _transparent: "transparent", // Background
  },

  _SIZES: {
    _title_1: "64px", // "4.0rem",
    _title_2: "52px", // "3.25rem",
    _title_3: "46px", // "2.875rem",
    _subtitle_1: "32px", // "2.0rem",
    _subtitle_2: "28px", // "1.75rem",
    _subtitle_3: "24px", // "1.5rem",
    _body: "16px", // "1.0rem",
  },

  _REGEXPS: {
    /** Busca espacios en blanco y cosas */
    _formatText: /(?:[\n\t\r\s]{2,})|(?:[\n\t\r])/g,
    /** Busca caracteres escapados */
    _escapedCharacters: /\\([\s\S])/g,
    /** Busca caracteres especiales de RegExp */
    _escapeRegExp: /[+*?^$.[\]{}()|/-]/gim,
    /** Busca e identifica propiedades dentro de `styleText` */
    _getProperties:
      /((?:[\S].*?)+?)(?::(?:({[^}]+})|([^,\n\t]+)))?(?:[,\n]|$)/g,
  },

  _URLS: {
    battleship:
      "https://brian101016-projects.netlify.app/web/battleship/battleship",
    minesweeper: "https://brian101016-projects.netlify.app/web/minesweeper/",
    "pvz-randomizer":
      "https://brian101016-projects.netlify.app/WEB/PVZ%20Randomizer/pvz_index.html",
    bingo:
      "https://brian101016-projects.netlify.app/WEB/Subnautica%20Bingo/index.html",
    "pokemon-calculator":
      "https://brian101016-projects.netlify.app/web/pokemon/",
    compiler:
      "https://brian101016-projects.netlify.app/WEB/JS%20Compiler/compiladores.html",
    cipher: "https://brian101016-projects.netlify.app/web/cifrado/cifrado",
    "horario-ues":
      "https://brian101016-projects.netlify.app/WEB/proyecto_web/index.html",
    "music-sheet": "https://brian101016-projects.netlify.app/web/partituras/",
    "chess-timer": "https://brian101016-projects.netlify.app/web/chess_timer/",
    "slide-puzzle":
      "https://brian101016-projects.netlify.app/web/puzzle/puzzle",
  },
};
